import { installmentAmount, totalWithFees, legacyFormFeeQuote, acceptsFeeSuggestion } from "./financial";
import React, { Fragment } from "react"
import {
    Alert,
    Button,
    Card,
    Divider,
    Form,
    Input,
    Modal,
    notification,
    Radio,
    Result,
    Row,
    Select,
    Space,
    Typography
} from "antd"
import { InfoCircleOutlined, WarningOutlined, CheckOutlined } from "@ant-design/icons"
import General from "./General"
import ApiService from "../../services/ApiService"
import Caisse from "./Caisse"
import Service from "./Service"
import Dette from "./Dette"
import FormSubmit from "./FormSubmit"
import Campagne from "./Campagne"
import ExtraFields from "./ExtraFields"
import {API_URL, DEBUG} from "../../config"
import PhoneField from "../WebEditor/components/PhoneInput"
import PhoneInput from "../WebEditor/components/PhoneInput"

import COUNTRY from "country-json/src/country-by-abbreviation.json"
import { applyI18n, getCurrencyFormater, getDeviseSymbol } from "../../i18n"
import MultiDettes from "./MultiDettes"
import {pledgeReceiptSummary} from '../../services/pledgeReceiptSummary.cjs'

import { t } from "@lingui/core/macro"
import { Trans } from "@lingui/react/macro"
import GiftAid from "./GiftAid";
import logger from "../../services/logger"
import { redirectToPlatform, navigateTop } from "../../services/paiementLauncher"
import AACFields from "./AACFields";
import {sepaCheckoutKey, mandateCheckoutUrl, isSepaXmlEnabledFor} from "../sepa_xml/paymentCheckout";
import {isKesherBitEnabled} from "./kesherEligibility.cjs";
import PaymentPlatformSelector from "./PaymentPlatformSelector";
import StripeWalletButtons from "./StripeWalletButtons";
import {oneOffCard, walletPrefill, walletMethodForPlatform, walletChoices, focusWalletField, hasWalletOptions} from "./stripeWalletProtocol";
import PaymentIframe from "./iframe/PaymentIframe";
import { getIframeProtocol } from "./iframe/protocols";
import { AccountAPI } from "../AccountSpace/api";
import {
    NEW_ADDRESS,
    buildInfoFromProfile,
    buildFacturationFromAddress,
    blankFacturationFields,
    buildAddressOptions,
    buildAddressPayloadFromForm,
} from "../AccountSpace/prefillPaiement";
import { isAccountPrefillDisabled, setAccountPrefillDisabled } from "../AccountSpace/accountPrefillToggle";
import AccountPrefillBanner from "../WebEditor/components/AccountPrefillBanner";

const { Text, Title } = Typography

export default class Paiement extends React.Component {

    state = {
        config: null,
        loading: false,
        name: "",
        initMem: false,
        iframePayment: null,
        iframeConfirming: false,
        // Espace compte (prefill) : compte titulaire connecté + adresse choisie dans
        // le select de facturation. `account` reste null si visiteur anonyme → le
        // tunnel se comporte exactement comme avant (prefill localStorage uniquement).
        account: null,        // {id, selfContact, profile:{nom,prenom,email,tel,homme,adresses[]}}
        addrChoice: null,     // id d'adresse sélectionnée, NEW_ADDRESS, ou null
        accountApplied: false,// garde anti double-application du prefill identité
        // Le visiteur a cliqué « ce n'est pas moi » → prefill compte désactivé pour
        // toute la session de navigation (sessionStorage, propagé form↔paiement).
        // Le compte reste chargé (réactivable) mais n'est pas appliqué.
        accountDisabled: isAccountPrefillDisabled(),
    }

    localStorage = {
        getItem: (v) => {},
        setItem: (k, v) => {}
    }

    componentDidMount() {
        this.setState({ config: this.props.config })
        this.localStorage = localStorage
        this.forceUpdate()

        // Prefill Espace compte : si un token de session compte existe, on récupère le
        // profil du TITULAIRE pour préremplir identité + proposer ses adresses. Best
        // effort, jamais bloquant : la moindre erreur laisse le tunnel en mode anonyme.
        this.loadAccount()

        let lockId=this.props?.config?.checkoutId ? null : this.props?.config?.campagne?.lockId
        if(lockId){
            logger.debug({ lockId }, "beforeunload unlock")

            this.unlock= () => {
                logger.debug({ lockId }, "unlock")
                navigator.sendBeacon(API_URL+"/campagne/unlock/"+lockId);
            }

            window.addEventListener("beforeunload", this.unlock);
            window.addEventListener("popstate", this.unlock);
            window.addEventListener("pagehide", this.unlock);
        }

    }

    componentWillUnmount() {
        if (this.unlock) this.unlock();

        // Nettoyage
        window.removeEventListener("beforeunload", this.unlock);
        window.removeEventListener("popstate", this.unlock);
        window.removeEventListener("pagehide", this.unlock);
    }

    // ── Prefill Espace compte ───────────────────────────────────────────
    //
    //  loadAccount  : récupère le profil titulaire si une session compte existe.
    //  applyAccountPrefill : pose identité + 1ʳᵉ adresse dans le form (une fois).
    //  onChangeAddress : applique l'adresse choisie dans le select de facturation.

    async loadAccount() {
        try {
            // Pas de gate getToken() : la session est un cookie HttpOnly (non lisible
            // en JS, partagé entre sous-domaines) → /me tranche (403 si anonyme).
            const res = await AccountAPI.me()
            // On ne prefill QUE si le compte est validé (selfContact rattaché).
            if (!res?.success || res?.account?.state !== "VALIDE") return
            const account = {
                id: res.account.id,
                selfContact: res.account.selfContact || null,
                profile: res.account.profile || {},
            }
            this.setState({account}, () => this.applyAccountPrefill())
        } catch (_) {
            // silencieux : mode anonyme conservé
        }
    }

    // Pose l'identité du titulaire (écrase le prefill localStorage : quand on est
    // connecté, le compte est la source de vérité) et sélectionne la 1ʳᵉ adresse.
    // Le form peut ne pas être monté au retour de /me (rendu conditionné par
    // state.config) → on retente brièvement tant que this.form est absent.
    applyAccountPrefill(attempt = 0) {
        const {account, accountApplied, accountDisabled} = this.state
        if (!account || accountApplied || accountDisabled) return
        if (!this.form) {
            if (attempt < 20) setTimeout(() => this.applyAccountPrefill(attempt + 1), 100)
            return
        }
        const profile = account.profile || {}

        const info = buildInfoFromProfile(profile)
        if (Object.keys(info).length) {
            this.form.setFieldsValue(info)
        }

        // Adresses : par défaut la 1ʳᵉ. On garde la valeur du select dans le state
        // (champ virtuel, pas un name= du form) + on applique ses champs facturation.
        const adresses = Array.isArray(profile.adresses) ? profile.adresses : []
        if (adresses.length > 0) {
            const first = adresses[0]
            const choice = first.id || "0"
            this.setState({addrChoice: choice})
            // setFieldsValue pilote le Radio `perso` ET le rendu conditionnel
            // raison/siren (qui lit getFieldValue("perso")) → pas de setState perso.
            this.form.setFieldsValue(buildFacturationFromAddress(first))
        }

        this.setState({accountApplied: true})
    }

    // Changement de sélection dans le select d'adresses.
    onChangeAddress(value) {
        if (this.state.loading) return
        this.setState({addrChoice: value})
        if (!this.form) return

        if (value === NEW_ADDRESS) {
            const org = this.props?.context?.org
            const defaultPays = COUNTRY.find(i => i.abbreviation === org?.country)?.country || "France"
            this.form.setFieldsValue(blankFacturationFields(defaultPays))
            return
        }

        const adresses = Array.isArray(this.state.account?.profile?.adresses) ? this.state.account.profile.adresses : []
        const addr = adresses.find((a, idx) => (a.id || String(idx)) === value)
        if (addr) {
            this.form.setFieldsValue(buildFacturationFromAddress(addr))
        }
    }

    // « Ce n'est pas moi » : on coupe l'usage du compte pour cette session (propagé
    // form↔paiement). On GARDE les valeurs déjà préremplies (juste déverrouillées) :
    // l'utilisateur corrige ce qu'il veut. config.contact ne sera plus envoyé.
    disableAccountPrefill() {
        if (this.state.loading) return
        setAccountPrefillDisabled(true)
        this.setState({accountDisabled: true})
    }

    // « Réutiliser mon compte » : on réactive et on ré-applique le prefill identité +
    // 1ʳᵉ adresse (accountApplied remis à false pour autoriser une nouvelle application).
    enableAccountPrefill() {
        if (this.state.loading) return
        setAccountPrefillDisabled(false)
        this.setState({accountDisabled: false, accountApplied: false}, () => this.applyAccountPrefill())
    }

    setService(service) {
        if (this.state.loading) return false
        const context = this.props.context
        const { org } = context

        const config = this.state.config
        if (!config || config.type != "SERVICE") {
            return false
        }

        let devise = config.devise || org.devise

        var newConfig = { ...config }
        newConfig.service = service
        this.setState({ config: newConfig })

        if (this.form) {
            var montant = service.montant
            if (devise !== (org.orgDevise || "eur") && service[devise]?.montant) {
                montant = service[devise].montant
            }

            if (Array.isArray(service.other) && service.other.length > 0) {
                montant += (montant * service.other.length)
            }
            this.form.setFieldValue("montant", montant)
        }
    }

    // Campagne dont les dons étalés se présentent en mensualité (hors don mensuel récurrent,
    // qui arrive avec son propre nbMonth).
    isCampagneMonthly() {
        const c = this.state.config || {}
        return c.type === "CAMPAGNE" && !!c.monthlyAmountDisplay && c.nbMonth === undefined && (c.maxDivide || 12) !== 1
    }

    getFinalValue(montant = 0) {
        return parseFloat(montant)
    }

    redirectPost(url, token) {
        const form = document.createElement("form");
        form.method = "POST";
        form.action = url;

        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "token";
        input.value = token;
        form.appendChild(input);

        document.body.appendChild(form);
        form.submit();
    }

    getPaymentPlatform(data, devise) {
        const {org} = this.props.context
        const config = this.state.config
        const recurrent = data.divide > 1 || (this.isCampagneMonthly()
            ? this.form.getFieldValue("nbMonth") > 1
            : this.form.getFieldValue("abo"))
        const recurrentPlatform = org?.web?.["paiement.platform.recurrent"]
        let platform = recurrent && recurrentPlatform ? recurrentPlatform : data.platform || config.platform

        const formData = config?.submit?.formData
        if (formData?.forceBankWithdrawal && platform !== "SEPA_XML") {
            const assoc = formData.assoc || "MAIN"
            if (isSepaXmlEnabledFor(org, assoc) && String(devise).toLowerCase() === 'eur') {
                platform = 'SEPA_XML'
            } else if (org.config.find(i => i.key === "GCL" && i.assoc === assoc)) {
                platform = "PRELEVEMENT.GOCARDLESS"
            } else if (org.config.find(i => i.key === "STRIPE.SEPA_PAIEMENT_METHOD" && i.assoc === assoc)) {
                platform = "STRIPE.SEPA"
            }
        }
        return platform
    }

    async onSubmit(data, devise, assoc, walletRequest = false) {
        const { org } = this.props.context

        // devise non fournie (ex: chemin ModalSuggestFees) -> on retombe sur la devise
        // déjà présente dans la config (multi-devises : ne JAMAIS perdre la devise réelle).
        if(!devise){
            devise = this.state.config?.devise || org.devise
        }

        if (this.state.loading && !walletRequest) {
            return false
        }

        if (!walletRequest) this.setState({ loading: true })

        logger.debug({ data }, "paiement onSubmit")

        var finalMontant = this.getFinalValue(data.montant)

        if (finalMontant < 1 && !this.state.config?.update) {
            notification.error({
                message: t`Le montant doit être au minimum de 1€`
            })
            // Sans ce reset, le garde-fou `if (this.state.loading)` ci-dessus bloquait
            // définitivement le bouton Valider après une erreur de montant.
            this.setState({ loading: false })
            return false
        }

        var config = { ...this.state.config, devise: devise }

        // Langue de l'interface au moment du paiement (context.langValue).
        // Stockée sur le paiement côté API -> pilote l'i18n du front paiement
        // et le tag de langue du contact (#FR/#EN/...).
        config.lang = this.props.context?.langValue || "fr"

        config.info = {
            tel: data.tel,
            email: data.email,
            nom: data.nom,
            prenom: data.prenom,
            homme: data.homme
        }
        this.localStorage.setItem("MEM_INFO", JSON.stringify(config.info))

        if (data.extra) {
            config.info.extra = data.extra
        }

        if (data.perso) {
            config.facturation = {
                perso: true,
                nom: data.nom,
                prenom: data.prenom,
                adresse: data.adresse,
                postal: data.postal,
                ville: data.ville,
                pays: data.pays
            }
        }

        if (!data.perso) {
            config.facturation = {
                perso: false,
                raison: data.raison,
                siren: data.siren,
                adresse: data.adresse,
                postal: data.postal,
                ville: data.ville,
                pays: data.pays
            }
        }

        if(data.etat){
            config.facturation.etat = data.etat
        }
        if (data.social_id) {
            config.facturation.social_id = data.social_id
        }
        if(data.giftAid){
            config.facturation.giftAid = data.giftAid
        }

        this.localStorage.setItem("MEM_FACTURATION", JSON.stringify({...config.facturation,giftAid:undefined}))

        config.name = this.state.name
        config.montant = data.montant
        config.commentaire = data.commentaire || null

        config.abo = this.form.getFieldValue("abo")
        config.nbMonth = parseInt(this.form.getFieldValue("nbMonth"))
        config.maxAmount = this.form.getFieldValue("maxAmount")

        // Campagne présentée en mensualité (cf. Campagne.js) : le montant saisi est déjà la
        // mensualité, la durée choisie fait l'abonnement ; « une seule fois » = paiement unique.
        if (this.isCampagneMonthly()) {
            config.abo = config.nbMonth > 1
            // Le formulaire affiche déjà une échéance : conserver la nature
            // fractionnée du don sans diviser à nouveau son montant.
            if (config.abo) {
                config.paymentMode = "SPLIT"
                config.divide = config.nbMonth
            }
            if (!config.abo) {
                config.nbMonth = 0
            }
        }

        if (config.type == "SERVICE") {
            config.service = this.state.config.service
        }

        if (config.type == "MULTI_DETTE") {
            delete config.dettes
            config.type = "GENERAL"
            config.typeId = null
            config.dettes = []

            //Enveloppe à répartir en cascade sur les promesses ouvertes. `montant` est en
            //EUROS, `maxAmount` en CENTIMES (cf. defaultValue.maxAmount plus bas) : ramener
            //à la même unité que les promesses, sinon le plafonnement ne joue jamais.
            //Enveloppe non exploitable -> on ne plafonne pas (comportement historique),
            //plutôt que de propager un NaN qui viderait la ventilation.
            let backMontant = config.abo ? config.maxAmount/100 : config.montant
            if(!Number.isFinite(backMontant)){
                backMontant = Infinity
            }

            for (let d of this.state.config.dettes) {
                if (backMontant <= 0) {
                    break
                }

                //Une promesse ne peut recevoir que ce qu'elle DOIT ENCORE (reste), jamais
                //son total : sinon une promesse déjà partiellement réglée absorbe l'enveloppe
                //et les promesses suivantes restent dues alors que le donateur a payé.
                //Côté abonnement, la somme des lignes doit égaler maxAmount pour que le
                //prorata de calculDette (ligne/abo.max × mensualité) reste juste.
                let detteMontant = (d.reste === undefined || d.reste === null) ? d.montant : d.reste
                if (detteMontant > backMontant) {
                    detteMontant = backMontant
                }
                backMontant -= detteMontant

                if (detteMontant > 0) {
                    config.dettes.push({ id: d.id, montant: detteMontant })
                }
            }

        } else {
            delete config.dettes
        }

        if (data.platform) {
            config.platform = data.platform
        }
        //AAC : seul aacAccountNumber est propre à AAC. Le reste est dérivé côté serveur
        //à partir de paiement.commentaire et paiement.campagne.displayName.
        if (config.platform === "AAC") {
            config.aacAccountNumber = data.aacAccountNumber || null
        }

        if (config.type === "CAMPAGNE") {
            const defaultCampagneConfig = config.campagne || {}
            config.campagne = {
                ...defaultCampagneConfig,
                ...data.campagne
            }
            if (data.selectedProductId) config.selectedProductId = data.selectedProductId
        }

        if (data.divide > 1) {
            config.abo = true
            config.nbMonth = data.divide
            if (config.type === "CAMPAGNE") {
                config.paymentMode = "SPLIT"
                config.divide = data.divide
            }
            config.montant = installmentAmount(data.montant, data.divide)
        }

        config.platform = this.getPaymentPlatform(data, devise)

        if (!walletRequest && walletMethodForPlatform(config.platform)) {
            this.setState({loading: false})
            return false
        }

        // ── Prefill Espace compte : rattachement contact + sauvegarde adresse ──
        //
        //  Si un compte titulaire est connecté (state.account), on rattache le
        //  paiement à SON contact via config.contact. Le backend revalide en garde
        //  A7 (createPaiementLink → resolveBoundContactId) : un id non rattaché est
        //  ignoré silencieusement. Anonyme OU « ce n'est pas moi » = pas de rattachement.
        if (config.platform !== "SEPA_XML" && this.state.account?.selfContact && !this.state.accountDisabled) {
            config.contact = this.state.account.selfContact

            // « Nouvelle adresse » choisie dans le select → on la persiste dans le
            // profil (et le contact) AVANT le paiement, comme demandé. Best effort :
            // un échec d'enregistrement ne bloque pas le paiement.
            if (!walletRequest && this.state.addrChoice === NEW_ADDRESS) {
                try {
                    const address = buildAddressPayloadFromForm(data)
                    if (address.adresse || address.ville || address.postal) {
                        const saved = await AccountAPI.addAddress(address)
                        // Rafraîchit le profil local (la nouvelle adresse reçoit un id
                        // serveur) pour que le select la liste si l'utilisateur revient.
                        if (saved?.success && saved.account?.profile) {
                            this.setState({
                                account: {...this.state.account, profile: saved.account.profile},
                                addrChoice: saved.address?.id || this.state.addrChoice,
                            })
                        }
                    }
                } catch (e) {
                    logger.error({ err: e }, "[paiement] addAddress error")
                }
            }
        }

        if (config.platform === "SEPA_XML") {
            if (!isSepaXmlEnabledFor(org, assoc || config.assoc || org.defaultAssoc || 'MAIN')) {
                this.setState({loading: false})
                notification.error({message: t`Prélèvement SEPA`, description: t`Impossible de terminer cette action. Vos informations sont conservées ; vous pouvez réessayer.`})
                return false
            }
            config.assoc = assoc || config.assoc || org.defaultAssoc || 'MAIN'
            config.organisation = {id: org.id}
            this.sepaXmlRequest = sepaCheckoutKey(config, this.sepaXmlRequest)
            config.sepaXmlRequestKey = this.sepaXmlRequest.key
        }

        if (walletRequest) return {...config, assoc: assoc || config.assoc || "MAIN"}

        logger.debug({ config: {...config, checkoutToken: config.checkoutToken ? "[redacted]" : undefined} }, "paiement CONFIG")

        if (DEBUG) {
            //return false
        }

        var res
        try {
            // Si un compte est rattaché (config.contact), router via le BFF
            // same-origin pour que le cookie de session parte → la garde A7 côté
            // API peut valider le contact. Sinon (anonyme), appel direct.
            const useProxy = !!config.contact
            res = await ApiService.post("/paiements/offline/link/", { config: JSON.stringify(config) }, { useProxy })
        } catch (e) {
            // L'Interceptor rejette sur erreur HTTP (timeout, 5xx…). Sans ce catch,
            // onSubmit lève en "Uncaught (in promise)" et loading reste bloqué à true
            // (bouton Valider figé). On normalise en une notification lisible.
            logger.error({ err: e }, "paiement offline/link error")
            notification.error({
                message: t`Erreur`,
                description: this.getErrorMessage(e)
            })
            this.setState({ loading: false })
            return false
        }

        if (res.success) {
            if (config.platform === 'SEPA_XML') {
                if (res.platform !== 'SEPA_XML' || !/^[a-zA-Z0-9_-]{43}$/.test(res.checkoutToken || '')) {
                    notification.error({message: t`Prélèvement SEPA`, description: t`Impossible de terminer cette action. Vos informations sont conservées ; vous pouvez réessayer.`})
                    this.setState({loading: false})
                    return false
                }
                redirectToPlatform(mandateCheckoutUrl(res.checkoutToken, window.location.origin, config.lang))
                this.setState({loading: false})
                return
            }
            //PLATEFORMES IFRAME (ex: Nedarim Plus) : pas de redirection. Si la plateforme
            //renvoyée a un protocole iframe enregistré, on affiche <PaymentIframe>. Les
            //paramètres spécifiques sont sous une clé nommée d'après la plateforme
            //(ex: res.nedarim). La confirmation passe par le CallBack serveur, confronté
            //ensuite par polling.
            const iframeProtocol = res.platform ? getIframeProtocol(res.platform) : null
            const iframeParams = res.platform ? res[res.platform.toLowerCase()] : null
            if (iframeProtocol && iframeParams) {
                this.setState({
                    loading: false,
                    iframePayment: {
                        platform: res.platform,
                        paiementId: res.paiementId,
                        isSubscription: res.isSubscription === true,
                        params: iframeParams
                    }
                })
                return
            }
            if(res.post){
               return this.redirectPost(res.url,res.token)
            }
            // Redirection vers la plateforme : web → window.location ; app native →
            // navigateur système via le bridge (retour par Universal Link). Cf.
            // services/paiementLauncher.redirectToPlatform.
            redirectToPlatform(res.url)
            this.setState({ loading: false })
        } else {
            // res.error peut être une string OU un objet d'erreur backend
            // ({name,status,data:{message},headers,res}). Le passer brut à
            // `description` faisait crasher React (#31 "objects are not valid as
            // a React child"). On extrait toujours un message texte.
            notification.error({
                message: t`Erreur`,
                description: this.getErrorMessage(res.error)
            })

            if(res.expired){
                setTimeout(() => {
                    if(config.returnUrl){
                        navigateTop(config.returnUrl)
                    }
                },5*1000)
            }

            this.setState({ loading: false })
        }
    }

    // Normalise une erreur (string | objet backend | AxiosError) en message
    // affichable. Ne renvoie JAMAIS un objet (sinon crash React au rendu).
    getErrorMessage(err) {
        if (!err) return t`Une erreur est survenue, veuillez réessayer.`
        if (typeof err === "string") return err
        // Erreur backend { name, status, data:{message}, ... } ou AxiosError.response
        const backend = err.data || err.response?.data
        if (backend) {
            if (typeof backend === "string") return backend
            if (typeof backend.message === "string") return backend.message
            if (typeof backend.error === "string") return backend.error
        }
        if (typeof err.message === "string") return err.message
        return t`Une erreur est survenue, veuillez réessayer.`
    }

    //URL de la page de succès (hébergée hors repo, cf. backend).
    buildSuccessUrl(paiementId) {
        const base = DEBUG
            ? "https://kehilapaiement.syn-assist.fr/"
            : "https://payment.kehila.syn-assist.fr/"
        const returnUrl = this.state.config?.returnUrl || "https://kehila.io"
        return base + "success/?source=WEB&returnUrl=" + encodeURIComponent(returnUrl) + "&id=" + paiementId + "&cerfa=1"
    }

    //PLATEFORMES IFRAME : après confirmation côté iframe, on poll l'état du paiement (posé
    //COMPLETED par le CallBack serveur), puis on redirige vers la page de succès. Absorbe le
    //décalage CallBack <-> réponse postMessage dans les deux sens. Générique (toute plateforme).
    //La redirection passe par navigateTop() : ici on est dans un timer, donc sans geste
    //utilisateur — un navigateur refuse alors de naviguer le top depuis une iframe d'une autre
    //origine, et le donateur resterait bloqué sur l'écran de paiement (cf. paiementLauncher).
    pollIframePaymentStatus(paiementId) {
        this.setState({ iframeConfirming: true })
        const started = Date.now()

        const tick = async () => {
            try {
                const r = await ApiService.get("/paiements/check/status/" + paiementId)
                if (r.success && r.status === "COMPLETED") {
                    navigateTop(this.buildSuccessUrl(paiementId))
                    return
                }
                if (["FAILED", "ERROR", "CANCELED"].includes(r.status)) {
                    notification.error({ message: t`Paiement non confirmé` })
                    this.setState({ iframePayment: null, iframeConfirming: false })
                    return
                }
            } catch (e) {
                logger.error({ err: e }, "IFRAME PAYMENT poll status")
            }

            if (Date.now() - started < 90 * 1000) {
                setTimeout(tick, 2500)
            } else {
                //Timeout : le CallBack n'est peut-être pas encore arrivé. On bascule quand même
                //vers la page de succès, qui re-vérifie l'état côté serveur.
                navigateTop(this.buildSuccessUrl(paiementId))
            }
        }

        tick()
    }

    getTitle() {
        var montant = ""
        var title = ""


        switch (this.state.config.type) {
            case "CAMPAGNE":
                title = t`Don pour ${this.state.config?.campagne?.label}`
                break
            case "GENERAL":
                title = t`Don pour ${this.state.config.organisation.shortName}`
                break
            case "CAISSE":
                title = t`Don pour ${this.state.config.caisse.name} chez ${this.state.config.organisation.shortName}`
                break
            case "DETTE":
            case "MULTI_DETTE":
                title = t`Règlement Promesse de don pour ${this.state.config.organisation.shortName}`
                break
            case "FORM":
                title = t`Règlement pour ${this.state.config.form.title}`
                break
            case "SERVICE":
                if (this.state.config.service.recurrent) {
                    title = t`Abonnement pour ${this.state.config.organisation.shortName}`
                } else {
                    title = t`Paiement pour ${this.state.config.organisation.shortName}`
                }
                break
        }

        if (this.state.config.update) {
            title = this.state.config?.paiement?.name
        }

        if (this.state.name != title) {
            this.setState({ name: title })
        }

        return title
    }

    getDescription() {
        const context = this.props.context
        const currencyFormater = getCurrencyFormater(context)

        var description = null

        switch (this.state.config.type) {
            case "CAISSE":
                description = <Text type="secondary">{this.state.config.caisse.description}</Text>
                break
            case "SERVICE":
                break
            case "DETTE":
                description = <Text type="secondary">
                    {t`${this.state.config.dette.name} (${currencyFormater(this.state.config.dette.montant, 0, this.state.config.dette.devise)})`}
                </Text>
                break
            case "FORM":
                description = this.state.config.form.description
                break
        }

        return description
    }

    getCardHeaderStyle(context, config) {
        let cardHeaderStyle = {}
        switch (context?.config?.theme) {
            case "DEFAULT":
                cardHeaderStyle.backgroundColor = "#281483"
                break
            case "UMMAH":
                cardHeaderStyle.backgroundColor = "#00b3d6"
                break
            case "2026":
                let secondary=context?.config?.theme2026?.tokens?.secondary
                cardHeaderStyle.backgroundColor = secondary || "#162127"
                break
            default:
                cardHeaderStyle.backgroundColor = "#162127"
        }

        if (config.type === "CAMPAGNE") {
            cardHeaderStyle.backgroundColor = config?.campagne?.theme || "#162127"
        }

        logger.debug({ travel: this.props.travel }, "paiement travel")
        if(this.props.travel?.theme?.secondaryColor){
            cardHeaderStyle.backgroundColor = this.props.travel?.theme?.secondaryColor || "#162127"
        }

        return cardHeaderStyle
    }

    getButtonsStyle(context, config) {
        let buttonStyle = {}
        switch (context?.config?.theme) {
            case "DEFAULT":
                buttonStyle.backgroundColor = "#281483"
                break
            case "UMMAH":
                buttonStyle.backgroundColor = "#00b3d6"
                break
            case "2026":
                let primary=context?.config?.theme2026?.tokens?.primary
                buttonStyle.backgroundColor = primary || "#162127"
                break
            default:
                buttonStyle.backgroundColor = "#162127"
        }

        if (config.type === "CAMPAGNE") {
            buttonStyle.backgroundColor = config?.campagne?.theme || "#162127"
        }

        logger.debug({ travel: this.props.travel }, "paiement travel")
        if(this.props.travel?.theme?.primaryColor){
            buttonStyle.backgroundColor = this.props.travel?.theme?.primaryColor || "#162127"
        }

        return buttonStyle
    }



/**
     * Bande d'identité affichée UNIQUEMENT quand la page de paiement s'ouvre en
     * pleine fenêtre alors que le site est configuré en mode intégré.
     *
     * C'est le cas du donateur qui vient de cliquer dans une iframe posée sur le
     * site de sa communauté : il quitte ce site et atterrit sur notre domaine,
     * sans en-tête, devant un formulaire de carte bancaire. Sans repère visible,
     * ce saut se paie en abandons. On lui remet donc le logo et le nom de son
     * organisation — rien de plus, la page reste sobre.
     */
    renderIdentityBand()
    {
        const context=this.props.context
        const {org}=context

        if(context.render !== "embed") return null
        if(typeof window === "undefined" || window.parent !== window.self) return null

        const name=org.shortName || org.name
        if(!name && !org.logo) return null

        return(
            <div style={{
                display:"flex",alignItems:"center",justifyContent:"center",gap:12,
                padding:"14px 10px 18px",marginBottom:10,
                borderBottom:"1px solid rgba(0,0,0,.08)"
            }}>
                {org.logo && (
                    <img
                        src={org.logo}
                        alt={name || ""}
                        style={{height:38,width:38,objectFit:"contain",borderRadius:8}}
                    />
                )}
                <div style={{display:"flex",flexDirection:"column",lineHeight:1.25}}>
                    <span style={{fontWeight:600,fontSize:15}}>{name}</span>
                    <span style={{fontSize:12.5,opacity:.65}}><Trans>Paiement sécurisé</Trans></span>
                </div>
            </div>
        )
    }

render()
    {
        const context=this.props.context
        const {org,T}=context

        let config=this.state.config

        if(config==null){
            return null
        }
        if(config.checkoutUnavailable){
            return <Result status="warning" title={t`Ce lien est expiré !`} subTitle={t`Pour toute question contactez directement votre responsable.`} />
        }

        const orgDevise=org.orgDevise
        let devise= config.devise || org.devise

        let defaultAssoc=org.defaultAssoc || "MAIN";

        if(devise!==(org.devise || "eur") && Array.isArray(org.devisesInterfaces))
        {
            const devisesInterface=org.devisesInterfaces.find(i=>i.devise===devise)
            if(devisesInterface && devisesInterface.defaultAssoc){
                defaultAssoc=devisesInterface.defaultAssoc
            }
        }

        let assoc=defaultAssoc

        logger.debug({ defaultAssoc }, "paiement defaultAssoc")


        //STYLE
        const cardHeaderStyle= this.getCardHeaderStyle(context,config)
        const buttonsStyle= this.getButtonsStyle(context,config)

        //console.log(config)

        var defaultMontant=1

        if(config.type==="SERVICE")
        {
            defaultMontant=config.service.montant
            assoc=config?.service?.assoc || "MAIN"

            let assocInfo=org.cerfasInfos.find(i=>i.id===assoc)
            if(assocInfo.devise && assocInfo.devise!==devise){
                if(config?.service[devise]?.assoc){
                    assoc=config?.service[devise]?.assoc
                }else{
                    assoc=defaultAssoc
                }
            }

            if(devise!==(orgDevise || "eur") && config?.service && config.service[devise]?.montant){
                defaultMontant=config.service[devise].montant
            }
        }

        if(config.type==="FORM")
        {
            defaultMontant=parseFloat(config.submit.reste/100).toFixed(2)
            assoc=config?.submit?.formData?.assoc || "MAIN"
            let assocInfo=org.cerfasInfos.find(i=>i.id===assoc)
            if(assocInfo.devise && assocInfo.devise!==devise){
                if(config?.submit[devise]?.assoc){
                    assoc=config?.submit[devise]?.assoc
                }else{
                    assoc=defaultAssoc
                }
            }
        }

        if(config.type==="CAISSE"){
            assoc=config?.caisse?.assoc || "MAIN"
            let assocInfo=org.cerfasInfos.find(i=>i.id===assoc)
            if(assocInfo.devise && assocInfo.devise!==devise){
                if(config?.caisse[devise]?.assoc){
                    assoc=config?.caisse[devise]?.assoc
                }else{
                    assoc=defaultAssoc
                }
            }
        }

        if(config.type==="DETTE"){
            assoc=config?.dette?.assoc || "MAIN"
            let assocInfo=org.cerfasInfos.find(i=>i.id===assoc)
            if(assocInfo.devise && assocInfo.devise!==devise){
                if(config?.dette[devise]?.assoc){
                    assoc=config?.dette[devise]?.assoc
                }else{
                    assoc=defaultAssoc
                }
            }
        }

        if(config.type==="MULTI_DETTE"){
            let _dette=null
            if(config.dettes?.length>0){
                _dette=config.dettes[0]
            }
            assoc=_dette?.assoc || "MAIN"
            let assocInfo=org.cerfasInfos.find(i=>i.id===assoc)
            if(assocInfo.devise && assocInfo.devise!==devise){
                if(_dette[devise]?.assoc){
                    assoc=_dette[devise]?.assoc
                }else{
                    assoc=defaultAssoc
                }
            }
        }


        let finalAssocInfo=org.cerfasInfos.find(i=>i.id===assoc)

        // Le SIREN est un identifiant purement français : on ne l'exige nommément
        // qu'en format français (FR, ou format absent = FR par défaut côté API).
        // Pour tout autre pays (IL, US, CA, RO…) on demande un « identifiant société »
        // générique — le champ reste le même (name="siren"), seul le libellé change.
        const isFrenchFormat = !finalAssocInfo?._format || finalAssocInfo._format==="FR"
        const companyIdLabel = isFrenchFormat ? t`Numero Siren` : t`Identifiant société`

        // Compte titulaire connecté ET non désactivé pour cette session : on VERROUILLE
        // l'identité civile (civilité, nom, prénom, email, tél) car le paiement est
        // rattaché à SON contact (config.contact + garde A7). Laisser modifier ces champs
        // créerait une incohérence identité↔contact. L'adresse de facturation reste
        // éditable (rôle du select d'adresses). Si « ce n'est pas moi » → tout déverrouillé.
        const accountActive = !!this.state.account && !this.state.accountDisabled
        const accountLocked = accountActive


        if(["GENERAL","CAISSE","CAMPAGNE"].includes(config.type) && config.montant){
            defaultMontant=config.montant
        }

        // Règlement de promesse : proposer le RESTE dû (unités), pas 1. Le donateur peut
        // toujours payer moins (lien réutilisable) ou plus.
        if(config.type==="DETTE" && config.dette && Number(config.dette.reste)>0){
            defaultMontant=Number(config.dette.reste)
        }
        if(config.type==="MULTI_DETTE" && Array.isArray(config.dettes)){
            const totalReste=config.dettes.reduce((s,d)=>s+(Number(d?.reste)||0),0)
            if(totalReste>0){
                defaultMontant=totalReste
            }
        }

        var defaultValue={perso:true,pays:COUNTRY.find(i=>i.abbreviation===org.country)?.country || "France",montant:defaultMontant,nbMonth:0,abo:false,divide:0,platform:"CARD"}

        if(config.type==="CAISSE" && config.caisse && config.caisse.smart)
        {
            defaultValue.nbMonth=0
        }

        if(config.type === "DETTE"){
            defaultValue.nbMonth=0
            defaultValue.maxAmount=Math.round(config.dette.reste * 100)
        }
        if(config.type === "MULTI_DETTE"){
            defaultValue.nbMonth=0
            let reste=0
            this.state.config.dettes.forEach(d=>{
                reste+=d.reste
            })
            defaultValue.maxAmount=Math.round(reste * 100)
        }

        if(config.type==="SERVICE" && config.service && config.service.recurrent)
        {
            defaultValue.nbMonth= config.service.nbMonth || 0
            defaultValue.abo=true
        }

        if(config.type==="CAMPAGNE")
        {
            assoc=config?.campagne?.assoc || "MAIN"

            if(config.divide){
                defaultValue.divide=config.divide
            }

            // Présentation en mensualité : un étalement choisi en amont arrive en abonnement
            // (total ÷ nb de mois, arrondi au supérieur comme le calcul d'onSubmit).
            if(this.isCampagneMonthly()){
                defaultValue.divide=0
                if(config.divide>1){
                    defaultValue.montant=installmentAmount(Number(defaultMontant),config.divide)
                    defaultValue.nbMonth=config.divide
                }
            }

            if(config.nbMonth!==undefined){
                defaultValue.nbMonth=config.nbMonth
                defaultValue.abo=true
            }
            if(config.checkoutId){
                defaultValue.abo=!!config.abo
                defaultValue.nbMonth=config.nbMonth || 0
                defaultValue.maxAmount=config.maxAmount || 0
                defaultValue.divide=0
            }
        }

        if(this.localStorage.getItem("MEM_FACTURATION")){
            try{
                var fac=JSON.parse(this.localStorage.getItem("MEM_FACTURATION"))
                defaultValue={...defaultValue,...fac}
                if(fac.perso!=this.state.perso && !this.state.initMem){
                    this.setState({initMem:true,perso:fac.perso})
                }
            }catch (e){}
        }

        if(this.localStorage.getItem("MEM_INFO")){
            try{
                var infos=JSON.parse(this.localStorage.getItem("MEM_INFO"))
                defaultValue={...defaultValue,...infos}
            }catch (e){}
        }

        //CAMPAGNE DISPLAY NAME
        if(defaultValue?.nom && defaultValue?.nom){
            defaultValue.campagne={
                displayName: defaultValue?.prenom + " " + defaultValue?.nom
            }
        }


        //PREFERED PLATFORM

        let PLATFORMS=[{label:t`Carte Bancaire`,value:"CARD",abo:false,image:"carte-bancaire.png"},{label:t`Carte Bancaire`,value:"CARD",abo:true,image:"carte-bancaire.png"}]
        if (String(devise).toLowerCase() === 'eur' && isSepaXmlEnabledFor(org, assoc)) {
            PLATFORMS.push({label: t`Prélèvement SEPA`, value: 'SEPA_XML', abo: true, image: 'compte-bancaire.png'})
        }
        if(org.config.find(i=>i.key==="GCL" && i.assoc===assoc)){
            PLATFORMS.push({label:t`Prélèvement Bancaire`,value:"PRELEVEMENT.GOCARDLESS",abo:true,image:"compte-bancaire.png"})
            /*
                if(["0fd10570-1a81-4fbe-9cbd-03995dc378ba","df084263-9b8f-46e9-aa47-f1ef7dd98f17"].includes(org.id)){
                    PLATFORMS.push({label:T("Virement"),value:"VIREMENT.GOCARDLESS",abo:false})
                }
             */
        }

        if(org.config.find(i=>i.key==="PAYPAL_PAIEMENT_METHOD" && i.assoc===assoc)){
            PLATFORMS.push({label:"Paypal",value:"PAYPAL",abo:false,image:"paypal.png"})
        }

        if(org.config.find(i=>i.key==="STRIPE.SEPA_PAIEMENT_METHOD" && i.assoc===assoc)){
            PLATFORMS.push({label:t`Prélèvement Bancaire`,value:"STRIPE.SEPA",abo:true,image:"compte-bancaire.png"})
        }

        if(finalAssocInfo?._format==="IL")
        {
            if(org.config.find(i=>i.key==="CARDCOM.BIT_PAIEMENT_METHOD" && i.assoc===assoc)){
                PLATFORMS.push({label:"BIT",value:"CARDCOM.BIT",abo:false})
            }

            if(isKesherBitEnabled(org, assoc, devise)){
                PLATFORMS.push({label:"Bit",value:"KESHER.BIT",abo:false,image:"bit.jpg"})
            }

            //Nedarim Plus n'est PAS une option de sélecteur : c'est la passerelle carte
            //standard (CARD), choisie en backend dès que l'asso a nedarimConfigId (comme
            //Cardcom). Le front détecte ensuite que la réponse est de type iframe (vs
            //redirect) et affiche <PaymentIframe> — cf. handler offline/link.

        }

        //AAC (Achisomoch) : UK uniquement, GBP uniquement, jamais en récurrent (V1)
        if(devise==="gbp" && org.config.find(i=>i.key==="AAC_PAIEMENT_METHOD" && i.assoc===assoc)){
            PLATFORMS.push({label:t`Donate via Achisomoch (AAC)`,value:"AAC",abo:false,image:"aac.png"})
        }

        //FORM FORCE BANK WITHDRAWAL : ne garder que les plateformes de prélèvement
        const forceBankWithdrawal = config?.submit?.formData?.forceBankWithdrawal === true
        if(forceBankWithdrawal){
            const BANK_PLATFORMS = ['SEPA_XML',"PRELEVEMENT.GOCARDLESS","STRIPE.SEPA","STRIPE.ACH"]
            PLATFORMS = PLATFORMS.filter(p => BANK_PLATFORMS.includes(p.value))
            if(PLATFORMS.length > 0){
                defaultValue.platform = PLATFORMS[0].value
            }
        }



        logger.debug({ devise }, "paiement DEVISE")
        logger.debug({ assoc, finalAssocInfo }, "paiement ASSOC")

        //PLATEFORMES IFRAME (ex: Nedarim Plus) : une fois le paiement initié, on remplace le
        //formulaire par l'iframe de la plateforme. Le composant est générique ; le protocole
        //est résolu depuis le registre selon la plateforme renvoyée par le backend.
        if(this.state.iframePayment){
            const protocol = getIframeProtocol(this.state.iframePayment.platform)
            if(protocol){
                return(
                    <PaymentIframe
                        protocol={protocol}
                        params={this.state.iframePayment.params}
                        onSuccess={()=>{
                            //Abonnement (HK) : aucun paiement immédiat à attendre (1er débit réconcilié
                            //plus tard par le serveur). On confirme directement. Paiement ponctuel : on
                            //poll le statut jusqu'à COMPLETED (le CallBack serveur le pose).
                            if(this.state.iframePayment.isSubscription){
                                navigateTop(this.buildSuccessUrl(this.state.iframePayment.paiementId))
                            }else{
                                this.pollIframePaymentStatus(this.state.iframePayment.paiementId)
                            }
                        }}
                        onError={(msg)=>{
                            notification.error({message:t`Paiement refusé`,description:msg})
                            this.setState({iframePayment:null,loading:false})
                        }}
                        onCancel={this.state.iframeConfirming ? null : ()=>this.setState({iframePayment:null,loading:false})}
                    />
                )
            }
        }

        return(
            <div className="unisoft-payment" style={{display:"flex",justifyContent:"center",alignItems:"center",paddingBottom:150,minWidth:0,maxWidth:"100%"}}>
                <style jsx global>{`
                  .unisoft-payment select.form-control {inline-size:100%;max-inline-size:100%;min-inline-size:0}
                  .unisoft-payment textarea {resize:none}
                `}</style>
                <div style={{padding:"0 10px",width:"100%"}}>
                    {this.renderIdentityBand()}
                    <Form noValidate disabled={this.state.loading}
                        onSubmitCapture={event => {
                            if (walletMethodForPlatform(this.form?.getFieldValue("platform"))) {
                                event.preventDefault(); event.stopPropagation()
                            }
                        }}
                        ref={(ref) => this.form = ref}
                        layout="vertical"
                        initialValues={defaultValue}
                        onValuesChange={changed => {
                            if (Object.prototype.hasOwnProperty.call(changed, "platform")) this.setState({walletMissingFields: []})
                        }}
                        onFinishFailed={(e) => {
                            notification.error({
                                message: t`Champs obligatoires`,
                                description: t`Veuillez renseigner tous les champs obligatoires avant de soumettre le paiement.`,
                                placement: "bottomRight"
                            })
                        }}
                        onFinish={(data) => {
                            if (this.state.loading || walletMethodForPlatform(data.platform)) return
                            const feeConfig = {...config, platform: this.getPaymentPlatform(data, devise)}
                            if(org?.web["suggestFees"]>0 && data?.montant>0 && acceptsFeeSuggestion(feeConfig))
                            {
                                this.modalSuggestFees.show(data,org.web["suggestFees"])
                            }else{
                                this.onSubmit(data,devise,assoc)
                            }
                        }}
                    >
                        {['DETTE','MULTI_DETTE'].includes(config.type) && <Form.Item noStyle shouldUpdate>
                            {form=>{
                                const quote=pledgeReceiptSummary(config,form.getFieldValue('montant'),
                                    form.getFieldValue('abo') ? form.getFieldValue('maxAmount') : undefined)
                                if(!quote) return null
                                if(config.cerfa===false) quote.fiscal=0
                                const format=getCurrencyFormater(context)
                                return <Alert type="info" showIcon message={quote.fiscal<quote.total ? t`Ce paiement comprend une part sans reçu fiscal.` : t`Reçu fiscal`}
                                    description={<dl>
                                        <dt><Trans>Part avec reçu fiscal</Trans></dt><dd>{format(quote.fiscal/100,2,devise)}</dd>
                                        <dt><Trans>Part sans reçu fiscal</Trans></dt><dd>{format((quote.total-quote.fiscal)/100,2,devise)}</dd>
                                    </dl>}/>
                            }}
                        </Form.Item>}
                        {!['DETTE','MULTI_DETTE'].includes(config.type) && config.cerfa == false && !config?.submit?.totalCerfa && (
                            <Alert
                                style={{ marginBottom: 20 }}
                                type="warning"
                                icon={<WarningOutlined />}
                                showIcon={true}
                                message={t`Ce paiement ne délivre pas de Reçu Fiscal !`}
                                description={t`Pour plus de renseignements demandez au responsable de votre association.`}
                            />
                        )}

                        {!['DETTE','MULTI_DETTE'].includes(config.type) && config.cerfa == true && (
                            <Alert
                                style={{ marginBottom: 20 }}
                                type="success"
                                icon={<CheckOutlined />}
                                showIcon={false}
                                message={t`Ce paiement délivre un reçu fiscal automatiquement !`}
                                description={t`Pour plus de renseignements demandez au responsable de votre association.`}
                            />
                        )}

                        {["DETTE","MULTI_DETTE"].includes(config.type) && (
                            <Alert
                                style={{ marginBottom: 20 }}
                                type="info"
                                icon={<InfoCircleOutlined />}
                                showIcon={false}
                                message={t`Ce lien de paiement est réutilisable !`}
                                description={t`Si vous préférez, vous pouvez régler une partie maintenant et revenir plus tard.`}
                            />
                        )}

                        <Form.Item noStyle shouldUpdate>
                            {walletForm => {
                                const walletMethod = walletMethodForPlatform(walletForm.getFieldValue("platform"))
                                const walletMode = !!walletMethod
                                const missing = this.state.walletMissingFields || []
                                const showField = name => !walletMode || missing.includes(name)
                                const billingMissing = missing.some(name => ["adresse", "postal", "ville", "pays", "etat", "raison", "siren", "social_id"].includes(name))
                                return <Fragment>
                        {!walletMode && !context.isMobile && (
                            <Form.Item style={{ display: "flex", justifyContent: "flex-end", marginBottom: 20 }}>
                                <Button onClick={() => this.form.submit()} size={"large"} style={{ color: "#FFF", ...buttonsStyle }} loading={this.state.loading}>
                                    {t`Valider`}
                                </Button>
                            </Form.Item>
                        )}

                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))", alignItems: "start", gap: 20, minWidth: 0 }}>

                            <Card headStyle={{ color: "#FFF", ...cardHeaderStyle }} style={{ width: "100%", minWidth: 0, maxWidth: "100%", boxShadow: "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px" }} title={config.cerfa == false ? t`Informations du Paiement` : t`Informations du Don`}>

                                <div style={{ padding: "10px 0", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                                    <Text strong style={{ textAlign: "center" }}>{this.getTitle()}</Text>

                                    {this.getDescription() &&(
                                        <div style={{ marginTop: 10, textAlign: "center" }}>
                                            {this.getDescription()}
                                        </div>
                                    )}
                                </div>

                                {config.type === "GENERAL" && (
                                    <General disabled={this.state.loading} devise={devise} config={config} context={context} />
                                )}

                                {config.type === "CAMPAGNE" && (
                                    <Campagne disabled={this.state.loading} devise={devise} config={config} context={context} />
                                )}

                                {config.type === "CAISSE" && (
                                    <Caisse disabled={this.state.loading} devise={devise} config={config} context={context} />
                                )}

                                {config.type === "SERVICE" && (
                                    <Service disabled={this.state.loading} devise={devise} config={config} setService={(service) => this.setService(service)} context={context} />
                                )}

                                {config.type === "DETTE" && (
                                    <Dette disabled={this.state.loading} devise={devise} config={config} context={context} />
                                )}

                                {config.type === "MULTI_DETTE" && (
                                    <MultiDettes disabled={this.state.loading} devise={devise} config={config} context={context} />
                                )}

                                {config.type === "FORM" && (
                                    <FormSubmit disabled={this.state.loading} devise={devise} config={config} context={context} />
                                )}

                                <Form.Item
                                    label={config.type === "CAMPAGNE" ? t`Commentaire ou Dédicace` : t`Commentaire`}
                                    name="commentaire"
                                >
                                    <Input.TextArea />
                                </Form.Item>

                                {config.type === "CAMPAGNE" && (
                                    <Form.Item
                                        label={t`Nom qui apparaitra`}
                                        name={["campagne", "displayName"]}
                                    >
                                        <Input placeholder={t`Vide = "Anonyme"`} autoComplete="given-name family-name" />
                                    </Form.Item>
                                )}

                                <Form.Item noStyle shouldUpdate>
                                    {(_form) => {
                                        const _abo = (_form.getFieldValue("abo") || _form.getFieldValue("divide") > 1 || (_form.getFieldValue("nbMonth") > 1 || config?.fixedNbMonth)) || false
                                        const _platforms = PLATFORMS.filter(i => i.abo === _abo)
                                        if (hasWalletOptions(org, assoc) && !_abo && !forceBankWithdrawal && finalAssocInfo?.stripeAvailable === true) {
                                            _platforms.push(...walletChoices(this.state.walletMethods, org, assoc).map(choice => ({...choice,
                                                label: choice.method === "apple_pay" ? t`Apple Pay` : t`Google Pay`})))
                                        }

                                        if (_abo && org?.web["paiement.platform.recurrent"]) {
                                            return true
                                        }

                                        const platform=_form.getFieldValue("platform")
                                        if(platform && _platforms.length && !_platforms.some(i=>i.value===platform)) {
                                            _form.setFieldValue("platform",_platforms[0].value)
                                        }

                                        return (
                                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%", minWidth: 0 }}>
                                                {_platforms.length > 1 && (
                                                    <Form.Item preserve={false} name={"platform"} label={t`Moyen de Paiement`}>
                                                        <PaymentPlatformSelector options={_platforms} />
                                                    </Form.Item>
                                                )}
                                                {_platforms.length === 1 && forceBankWithdrawal && (
                                                    <Form.Item preserve={false} name={"platform"} label={t`Moyen de Paiement`}>
                                                        <PaymentPlatformSelector options={_platforms} />
                                                    </Form.Item>
                                                )}

                                            </div>
                                        )
                                    }}
                                </Form.Item>

                            </Card>

                            <Card headStyle={{ color: "#FFF", ...cardHeaderStyle }} style={{ width: "100%", minWidth: 0, maxWidth: "100%", boxShadow: "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px" }} title={t`Informations du Contact`}>

                                {accountActive && (
                                    <AccountPrefillBanner
                                        mode="active"
                                        title={(() => {
                                            const p = this.state.account.profile || {}
                                            const full = [p.prenom, p.nom].filter(Boolean).join(" ").trim()
                                            return full ? t`Connecté en tant que` + " " + full : t`Vous êtes connecté à votre compte`
                                        })()}
                                        subtitle={t`Vos informations sont pré-remplies`}
                                        actionLabel={t`Ce n'est pas moi`}
                                        onAction={() => this.disableAccountPrefill()}
                                    />
                                )}

                                {this.state.account && this.state.accountDisabled && (
                                    <AccountPrefillBanner
                                        mode="disabled"
                                        title={t`Vous saisissez d'autres informations`}
                                        subtitle={t`Ce don ne sera pas rattaché à votre compte`}
                                        actionLabel={t`Réutiliser mon compte`}
                                        onAction={() => this.enableAccountPrefill()}
                                    />
                                )}

                                <Form.Item
                                    label={t`Civilité`}
                                    name="homme"
                                    rules={[{ required: true, message: t`Ce champ est obligatoire` }]}
                                >
                                    <Select size="large" placeholder={t`Civilité`} disabled={accountLocked || this.state.loading}>
                                        <Select.Option value={true}>{t`Monsieur`}</Select.Option>
                                        <Select.Option value={false}>{t`Madame`}</Select.Option>
                                    </Select>
                                </Form.Item>

                                <Form.Item
                                    label={t`Prénom`}
                                    name="prenom"
                                    rules={[{ required: true, message: t`Ce champ est obligatoire` }]}
                                >
                                    <Input placeholder={t`Prénom`} autoComplete="given-name" disabled={accountLocked || this.state.loading} />
                                </Form.Item>

                                <Form.Item
                                    label={t`Nom`}
                                    name="nom"
                                    rules={[{ required: true, message: t`Ce champ est obligatoire` }]}
                                >
                                    <Input placeholder={t`Nom`} autoComplete="family-name" disabled={accountLocked || this.state.loading} />
                                </Form.Item>

                                <Form.Item
                                    hidden={!showField("email")}
                                    label={t`Email`}
                                    name="email"
                                    rules={[{ required: true, message: t`Ce champ est obligatoire` }, { type: "email", message: t`Cette adresse email n'est pas valide` }]}
                                >
                                    <Input type="email" placeholder={t`Email`} autoComplete="email" disabled={accountLocked || this.state.loading} />
                                </Form.Item>

                                <Form.Item
                                    hidden={!showField("tel")}
                                    label={t`Téléphone`}
                                    name="tel"
                                    rules={[{ required: true, message: t`Ce champ est obligatoire` }, { validator: (_, _value) => !isNaN(_value) ? Promise.resolve(true) : Promise.reject(t`Le numéro de téléphone est invalide`) }]}
                                >
                                    <PhoneInput defaultLang={context.langValue} disabled={accountLocked || this.state.loading} />
                                </Form.Item>

                                <ExtraFields org={config.organisation} />
                                {walletMode && <Form.Item label={t`Type de Formulaire`} name="perso">
                                    <Radio.Group>
                                        <Radio.Button value={true}>{t`Personnel`}</Radio.Button>
                                        <Radio.Button value={false}>{t`Entreprise`}</Radio.Button>
                                    </Radio.Group>
                                </Form.Item>}
                                <StripeWalletButtons
                                    enabled={hasWalletOptions(org, assoc) && !forceBankWithdrawal && oneOffCard({...walletForm.getFieldsValue(true), platform: "CARD"}, config) && finalAssocInfo?.stripeAvailable === true}
                                    selectedMethod={walletMethod}
                                    onAvailability={methods => {
                                        const old = this.state.walletMethods || {}
                                        if (old.applePay !== methods.applePay || old.googlePay !== methods.googlePay) {
                                            this.setState({walletMethods: methods})
                                        }
                                        const selected = walletMethodForPlatform(walletForm.getFieldValue("platform"))
                                        if (!this.state.loading && selected && !(selected === "apple_pay" ? methods.applePay : methods.googlePay)) {
                                            walletForm.setFieldValue("platform", "CARD")
                                        }
                                    }}
                                    organisation={org.id} assoc={assoc} currency={String(devise).toLowerCase()}
                                    amount={Math.round(Number(walletForm.getFieldValue("montant")) * 100)}
                                    disabled={this.state.loading} authenticated={accountActive}
                                    onBusy={loading => this.setState({loading})}
                                    getIdentity={() => walletForm.getFieldsValue(true)}
                                    validateIdentity={() => walletForm.validateFields(["homme", "prenom", "nom", ...(walletForm.getFieldValue("perso") === false ? ["raison", "siren"] : [])])}
                                    buildConfig={() => this.onSubmit(walletForm.getFieldsValue(true), devise, assoc, true)}
                                    applyDetails={details => {
                                        const values = {...walletForm.getFieldsValue(true)}
                                        if (!walletForm.isFieldTouched("pays") && !values.adresse) values.pays = ""
                                        walletForm.setFieldsValue(walletPrefill(values, details))
                                    }}
                                    validate={() => walletForm.validateFields()}
                                    onMissing={fields => {
                                        const names = (fields || []).map(field => Array.isArray(field) ? field[0] : String(field).split(".").pop())
                                        this.setState({walletMissingFields: names}, () => {
                                            const field = fields?.[0]
                                            const name = Array.isArray(field) ? field : String(field || "prenom").split(".").pop()
                                            requestAnimationFrame(() => focusWalletField(walletForm, name))
                                        })
                                    }}
                                />
                                {walletMode && finalAssocInfo?._format === "UK" && <GiftAid assocInfo={finalAssocInfo}/>}
                            </Card>

                            <Card headStyle={{ color: "#FFF", ...cardHeaderStyle }} style={{ display: walletMode && walletForm.getFieldValue("perso") !== false && !billingMissing ? "none" : undefined, width: "100%", minWidth: 0, maxWidth: "100%", boxShadow: "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px" }} title={config.cerfa == false ? t`Adresse de Facturation` : t`Informations du Reçu Fiscal`}>

                                {/* Sélecteur d'adresses (compte connecté ET actif uniquement) : liste les
                                    adresses du profil titulaire + « Nouvelle adresse ». Choisir une adresse
                                    remplit les champs ci-dessous ; « Nouvelle adresse » les vide et l'adresse
                                    saisie sera enregistrée dans le profil au Valider. Masqué si « ce n'est
                                    pas moi » (saisie 100% libre). */}
                                {!walletMode && accountActive && Array.isArray(this.state.account.profile?.adresses) && this.state.account.profile.adresses.length > 0 && (
                                    <Form.Item label={t`Choisir une adresse`}>
                                        <select
                                            className={"form-control"}
                                            value={this.state.addrChoice || ""}
                                            disabled={this.state.loading}
                                            onChange={(e) => this.onChangeAddress(e.target.value)}
                                        >
                                            {buildAddressOptions(this.state.account.profile, {
                                                newAddress: t`+ Nouvelle adresse`,
                                                proPrefix: t`(entreprise)`,
                                                address: t`Adresse`,
                                            }).map(o => (
                                                <option key={o.value} value={o.value}>{o.label}</option>
                                            ))}
                                        </select>
                                    </Form.Item>
                                )}

                                {!walletMode && <Form.Item label={t`Type de Formulaire`} name="perso">
                                    <Radio.Group>
                                        <Radio.Button value={true}>{t`Personnel`}</Radio.Button>
                                        <Radio.Button value={false}>{t`Entreprise`}</Radio.Button>
                                    </Radio.Group>
                                </Form.Item>}

                                <Form.Item noStyle shouldUpdate>
                                    {(_form) =>
                                    {
                                        const isPerso = _form.getFieldValue("perso") || false
                                        if (!isPerso)
                                        {
                                            return(
                                                <Fragment>
                                                    <Form.Item
                                                        label={t`Raison Sociale`}
                                                        name="raison"
                                                        rules={[{ required: true, message: t`Ce champ est obligatoire` }]}
                                                    >
                                                        <Input placeholder={t`Raison Sociale`} autoComplete="organization" />
                                                    </Form.Item>

                                                    <Form.Item
                                                        label={companyIdLabel}
                                                        name="siren"
                                                        rules={[{ required: true, message: t`Ce champ est obligatoire` }]}
                                                    >
                                                        <Input placeholder={companyIdLabel} />
                                                    </Form.Item>
                                                </Fragment>
                                            )
                                        }

                                        if(isPerso && finalAssocInfo?._format==="IL"){
                                            return (
                                                <Form.Item
                                                    label={t`Numéro d’identité`}
                                                    name="social_id"
                                                    rules={[{ required: true, message: t`Ce champ est obligatoire` }]}
                                                    help={t`Si vous n'avez pas de numéro d’identité mettez: 00000000`}
                                                >
                                                    <Input placeholder={t`Numéro d’identité ou 00000000`} />
                                                </Form.Item>
                                            )
                                        }

                                        return null
                                    }}
                                </Form.Item>

                                <Form.Item
                                    hidden={!showField("adresse")}
                                    label={t`Adresse`}
                                    name="adresse"
                                    rules={[{ required: true, message: t`Ce champ est obligatoire` }]}
                                >
                                    <Input placeholder={t`Adresse`} autoComplete="address-line1" />
                                </Form.Item>

                                {finalAssocInfo?._format!=="IL" &&(
                                    <Form.Item
                                        hidden={!showField("postal")}
                                        label={t`Code Postal`}
                                        name="postal"
                                        rules={[{ required: true, message: t`Ce champ est obligatoire` }]}
                                    >
                                        <Input placeholder={t`Code Postal`} autoComplete="postal-code" />
                                    </Form.Item>
                                )}

                                <Form.Item
                                    hidden={!showField("ville")}
                                    label={t`Ville`}
                                    name="ville"
                                    rules={[{ required: true, message: t`Ce champ est obligatoire` }]}
                                >
                                    <Input placeholder={t`Ville`} autoComplete="address-level2" />
                                </Form.Item>

                                {["US","CA"].includes(finalAssocInfo?._format) &&(
                                    <Form.Item
                                        hidden={!showField("etat")}
                                        label={t`État / Province`}
                                        name="etat"
                                        rules={[{ required: true, message: t`Ce champ est obligatoire` }]}
                                    >
                                        <Input placeholder={t`État / Province`} autoComplete="state" />
                                    </Form.Item>
                                )}

                                <Form.Item
                                    hidden={!showField("pays")}
                                    label={t`Pays`}
                                    name="pays"
                                    rules={[{required: true, message: t`Ce champ est obligatoire`}]}
                                >
                                    <select className={"form-control"} disabled={this.state.loading}>
                                        <option unselectable disabled>{t`Pays`}</option>
                                        {COUNTRY.map(i => {
                                            return (
                                                <option value={i.country}>{i.country}</option>
                                            )
                                        })}
                                    </select>
                                </Form.Item>


                                {!walletMode && <GiftAid assocInfo={finalAssocInfo}/>}
                                <AACFields/>
                            </Card>
                        </div>

                        {!walletMode && !context.isMobile && (
                            <Form.Item style={{display: "flex", justifyContent: "flex-end", marginTop: 20}}>
                                <Button onClick={() => this.form.submit()} size={"large"}
                                        style={{color: "#FFF", ...buttonsStyle}} loading={this.state.loading}>
                                    {t`Valider`}
                                </Button>
                            </Form.Item>
                        )}

                        {!walletMode && context.isMobile && (
                            <Button onClick={() => this.form.submit()} size={"large"} block htmlType="submit" style={{ color: "#FFF", marginTop: 20, ...buttonsStyle }} loading={this.state.loading}>
                                {t`Valider`}
                            </Button>
                        )}

                                </Fragment>
                            }}
                        </Form.Item>
                    </Form>

                    <ModalSuggestFees context={context} getRef={(ref)=>this.modalSuggestFees=ref} onFinish={(data)=>this.onSubmit(data,devise,assoc)} />
                </div>
            </div>
        )

    }
}



class ModalSuggestFees extends React.Component {

    constructor(props) {
        super(props);
        props.getRef(this)
    }

    initialState = {
        visible: false,
        initialData: {},
        initialMontant: 0,
        finalMontant: 0,
        finalFees: 0,
    };

    state = this.initialState

    show(data, minFeesValue) {
        const initialMontant = parseFloat(data.montant)
        const quote = legacyFormFeeQuote(data, minFeesValue)
        const finalMontant = quote.total
        const finalFees = quote.rawFees

        if (!quote.offered) {
            this.props.onFinish(data)
            return false
        }

        this.setState({ initialMontant, finalMontant, finalFees, visible: true, initialData: data })
    }

    calculateTotalWithFees(initialMontant, percentageFee, fixedFee) {
        return totalWithFees(initialMontant, percentageFee, fixedFee);
    }

    onValidate(accept) {
        if (accept) {
            const nd = { ...this.state.initialData }
            nd.montant = parseFloat(nd.montant) + (Math.round(this.state.finalFees * 100) / 100)
            this.props.onFinish(nd)
        } else {
            this.props.onFinish({ ...this.state.initialData })
        }
        this.close()
    }

    close() {
        this.setState(this.initialState)
    }

    render() {
        const context = this.props.context
        const { org } = context

        const currencyFormater = getCurrencyFormater(context)

        return (
            <Modal
                zIndex={30000000}
                width={context.isMobile ? "100%" : undefined}
                open={this.state.visible}
                destroyOnClose
                closable={false}
                footer={null}
                onCancel={() => { }}
                title={<Title level={context.isMobile ? 5 : 4}><Trans>Souhaitez-vous prendre en charge les frais bancaires de l'association ?</Trans></Title>}
            >
                <Space direction="vertical" style={{ marginTop: 20 }}>
                    <Text>
                        <Trans>
                            Après déduction des frais bancaires de votre don de <Text strong>{currencyFormater(this.state.initialMontant, 2)}</Text>,<br />
                            l'association recevra <Text strong>{currencyFormater(this.state.initialMontant - this.state.finalFees, 2)}</Text>.
                        </Trans>
                    </Text>
                    <Text>
                        <Trans>
                            Souhaitez-vous inclure les frais bancaires de <Text strong>{currencyFormater(this.state.finalFees, 2)}</Text> pour que l'association puisse bénéficier pleinement de votre don ?
                        </Trans>
                    </Text>
                </Space>

                <Row justify="center" align="middle" style={{ marginTop: 20 }}>
                    <Space direction={context.isMobile ? "vertical" : "horizontal"}>
                        <Button onClick={() => this.onValidate(false)} size="large">
                            <Trans>Je garde le montant initial</Trans>
                        </Button>
                        <Button onClick={() => this.onValidate(true)} size="large" type="primary">
                            <Trans>J'ajoute les frais bancaires</Trans>
                        </Button>
                    </Space>
                </Row>
            </Modal>
        )
    }
}
